import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import TexHubLogo from '@/components/brand/TexHubLogo';
import { 
  ArrowRight, Menu, Store, Tag, Package, Users, MessageSquare, 
  BarChart3, Sparkles, CheckCircle2, Globe, ChevronRight,
  Shield, Zap, TrendingUp, Star
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const { isUSA, changeRegion } = useLocale();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) return null;

  const t = {
    nav: {
      features: isUSA ? 'Features' : 'Funciones',
      pricing: isUSA ? 'Pricing' : 'Precios',
      login: isUSA ? 'Log in' : 'Iniciar sesión',
      start: isUSA ? 'Start free' : 'Comenzar gratis'
    },
    hero: {
      badge: isUSA ? '✨ AI-Powered Platform' : '✨ Plataforma Inteligente',
      title1: isUSA ? 'The smart platform' : 'La plataforma inteligente',
      title2: isUSA ? 'for the textile industry' : 'para la industria textil',
      subtitle: isUSA 
        ? 'Connect with suppliers and buyers, create AI-powered designs, analyze trends, and grow your textile business with cutting-edge technology.'
        : 'Conecta con proveedores y compradores, crea diseños con IA, analiza tendencias y haz crecer tu negocio textil con tecnología de vanguardia.',
      cta: isUSA ? 'Start free trial' : 'Comenzar prueba gratis',
      ctaSecondary: isUSA ? 'Watch demo' : 'Ver demo',
      trust: isUSA ? 'Free to start • No credit card required' : 'Gratis para empezar • Sin tarjeta de crédito'
    },
    ai: {
      badge: isUSA ? 'AI Tools' : 'Herramientas IA',
      title: isUSA ? 'TexHub AI: Design the future' : 'TexHub AI: Diseña el futuro',
      subtitle: isUSA 
        ? 'Powerful artificial intelligence tools to accelerate your creative process and optimize your catalog'
        : 'Potentes herramientas de inteligencia artificial para acelerar tu proceso creativo y optimizar tu catálogo',
      tools: isUSA ? [
        { name: 'Fashion Designer AI', desc: 'Generate garment designs from text descriptions', credits: 5 },
        { name: 'Visual Search', desc: 'Find products by uploading an image', credits: 2 },
        { name: 'Photo Optimizer', desc: 'Enhance your product photos with AI', credits: 3 },
        { name: 'TexTrend AI', desc: 'Analyze fashion trends and get insights', credits: 4 },
        { name: 'Fabric Applicator', desc: 'Apply fabric textures to designs', credits: 4 },
        { name: 'Pattern Generator', desc: 'Create unique textile patterns', credits: 5 }
      ] : [
        { name: 'Diseñador de Modas IA', desc: 'Genera diseños de prendas desde descripciones de texto', credits: 5 },
        { name: 'Buscador Visual', desc: 'Encuentra productos subiendo una imagen', credits: 2 },
        { name: 'Optimizador de Fotos', desc: 'Mejora tus fotos de producto con IA', credits: 3 },
        { name: 'TexTrend AI', desc: 'Analiza tendencias de moda y obtén insights', credits: 4 },
        { name: 'Aplicador de Telas', desc: 'Aplica texturas de tela a tus diseños', credits: 4 },
        { name: 'Generador de Estampados', desc: 'Crea patrones textiles únicos', credits: 5 }
      ]
    },
    features: {
      title: isUSA ? 'Everything you need to grow' : 'Todo lo que necesitas para crecer',
      subtitle: isUSA 
        ? 'Powerful tools designed for the textile industry'
        : 'Herramientas poderosas diseñadas para la industria textil'
    },
    store: {
      badge: isUSA ? 'Online Store' : 'Tienda en Línea',
      title: isUSA ? 'Your own online store' : 'Tu propia tienda en línea',
      subtitle: isUSA 
        ? 'Create a professional storefront in minutes. Share your link and start receiving orders from anywhere.'
        : 'Crea una tienda profesional en minutos. Comparte tu link y empieza a recibir pedidos desde cualquier lugar.',
      features: isUSA ? [
        'Custom store link (texhub.com.mx/tu-tienda)',
        'Receive orders directly',
        'Integrated shopping cart',
        'Professional product catalog'
      ] : [
        'Link personalizado (texhub.com.mx/tu-tienda)',
        'Recibe pedidos directamente',
        'Carrito de compras integrado',
        'Catálogo de productos profesional'
      ]
    },
    surplus: {
      badge: isUSA ? 'Surplus & Deals' : 'Saldos y Remates',
      title: isUSA ? 'Turn inventory into cash' : 'Convierte inventario en efectivo',
      subtitle: isUSA 
        ? 'Publish your fabric and apparel surplus. Reach verified buyers looking for deals.'
        : 'Publica tus saldos de tela y ropa. Llega a compradores verificados buscando oportunidades.',
      features: isUSA ? [
        'Fabric deals',
        'Apparel deals',
        'No commissions',
        'Instant visibility'
      ] : [
        'Saldos de tela',
        'Saldos de ropa',
        'Sin comisiones',
        'Visibilidad inmediata'
      ]
    },
    sections: {
      title: isUSA ? 'One platform, all your needs' : 'Una plataforma, todas tus necesidades',
      items: isUSA ? [
        { icon: <Store className="w-6 h-6" />, title: 'Online Store', desc: 'Your own e-commerce' },
        { icon: <Tag className="w-6 h-6" />, title: 'Surplus', desc: 'Sell overstock fast' },
        { icon: <Package className="w-6 h-6" />, title: 'Catalog', desc: 'Digital product showcase' },
        { icon: <MessageSquare className="w-6 h-6" />, title: 'Quotes', desc: 'Receive inquiries' },
        { icon: <Sparkles className="w-6 h-6" />, title: 'TexHub AI', desc: 'AI-powered design tools' },
        { icon: <BarChart3 className="w-6 h-6" />, title: 'Analytics', desc: 'Track performance' }
      ] : [
        { icon: <Store className="w-6 h-6" />, title: 'Tienda en Línea', desc: 'Tu propio e-commerce' },
        { icon: <Tag className="w-6 h-6" />, title: 'Saldos', desc: 'Vende excedentes rápido' },
        { icon: <Package className="w-6 h-6" />, title: 'Catálogo', desc: 'Vitrina de productos' },
        { icon: <MessageSquare className="w-6 h-6" />, title: 'Cotizaciones', desc: 'Recibe solicitudes' },
        { icon: <Sparkles className="w-6 h-6" />, title: 'TexHub AI', desc: 'Herramientas de diseño IA' },
        { icon: <BarChart3 className="w-6 h-6" />, title: 'Analytics', desc: 'Mide tu desempeño' }
      ]
    },
    cta: {
      title: isUSA ? 'Ready to grow your business?' : '¿Listo para hacer crecer tu negocio?',
      subtitle: isUSA 
        ? 'Join hundreds of textile companies already selling more with TexHub'
        : 'Únete a cientos de empresas textiles que ya venden más con TexHub',
      button: isUSA ? 'Get started for free' : 'Comenzar gratis'
    },
    footer: {
      tagline: isUSA ? 'The smart platform for the textile industry' : 'La plataforma inteligente para la industria textil',
      available: isUSA ? 'Available in Mexico and United States' : 'Disponible en México y Estados Unidos'
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar - Shopify style */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo original */}
            <Link to="/" className="flex items-center">
              <img 
                src={scrolled ? "/logo-texhub.png" : "/logo-texhub-hero.png"}
                alt="TexHub" 
                className="h-10 object-contain"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              <a href="#features" className={`text-sm font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-white/80 hover:text-white'}`}>
                {t.nav.features}
              </a>
              <button onClick={() => navigate('/pricing')} className={`text-sm font-medium transition-colors ${scrolled ? 'text-slate-600 hover:text-slate-900' : 'text-white/80 hover:text-white'}`}>
                {t.nav.pricing}
              </button>
            </div>

            {/* Right side */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Region selector + Login together */}
              <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 ${scrolled ? 'bg-slate-100' : 'bg-white/10 backdrop-blur-sm'}`}>
                <button 
                  onClick={() => changeRegion('MX')}
                  className={`p-1 rounded-md transition-all ${!isUSA ? (scrolled ? 'bg-slate-200' : 'bg-white/30') : (scrolled ? 'hover:bg-slate-200' : 'hover:bg-white/20')}`}
                >
                  <span className="text-base">🇲🇽</span>
                </button>
                <button 
                  onClick={() => changeRegion('US')}
                  className={`p-1 rounded-md transition-all ${isUSA ? (scrolled ? 'bg-slate-200' : 'bg-white/30') : (scrolled ? 'hover:bg-slate-200' : 'hover:bg-white/20')}`}
                >
                  <span className="text-base">🇺🇸</span>
                </button>
                <div className={`w-px h-5 mx-1 ${scrolled ? 'bg-slate-300' : 'bg-white/30'}`} />
                <button 
                  onClick={() => navigate('/login')}
                  className={`text-sm font-medium px-2 ${scrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/90 hover:text-white'}`}
                >
                  {isUSA ? 'Log in' : 'Entrar'}
                </button>
              </div>
              
              <Button 
                onClick={() => navigate('/register')}
                className="bg-[#34B772] hover:bg-[#2da366] text-white font-medium px-5"
              >
                {t.nav.start}
              </Button>
            </div>

            {/* Mobile menu */}
            <div className="lg:hidden flex items-center gap-2">
              <div className={`flex items-center gap-1 rounded-full px-2 py-1 ${scrolled ? 'bg-slate-100' : 'bg-white/10 backdrop-blur-sm'}`}>
                <button onClick={() => changeRegion('MX')} className="p-1">
                  <span className="text-base">🇲🇽</span>
                </button>
                <button onClick={() => changeRegion('US')} className="p-1">
                  <span className="text-base">🇺🇸</span>
                </button>
                <div className={`w-px h-4 mx-1 ${scrolled ? 'bg-slate-300' : 'bg-white/30'}`} />
                <button 
                  onClick={() => navigate('/login')}
                  className={`text-xs font-medium px-1 ${scrolled ? 'text-slate-700' : 'text-white/90'}`}
                >
                  {isUSA ? 'Log in' : 'Entrar'}
                </button>
              </div>
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className={scrolled ? 'text-slate-900' : 'text-white'}>
                    <Menu className="w-6 h-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 p-0">
                  <div className="flex flex-col h-full">
                    <div className="p-6 border-b">
                      <TexHubLogo size="small" />
                    </div>
                    <nav className="flex-1 p-6">
                      <div className="space-y-4">
                        <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-medium text-slate-900">
                          {t.nav.features}
                        </a>
                        <button onClick={() => { setMobileMenuOpen(false); navigate('/pricing'); }} className="block text-lg font-medium text-slate-900 text-left w-full">
                          {t.nav.pricing}
                        </button>
                      </div>
                    </nav>
                    <div className="p-6 border-t space-y-3">
                      <Button variant="outline" className="w-full" onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}>
                        {t.nav.login}
                      </Button>
                      <Button className="w-full bg-[#34B772] hover:bg-[#2da366]" onClick={() => { setMobileMenuOpen(false); navigate('/register'); }}>
                        {t.nav.start}
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: 'url("https://images.unsplash.com/photo-1558171813-4c088753af8f?auto=format&fit=crop&w=2000&q=80")'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-slate-800/60 to-slate-900/65" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 lg:pt-32 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Content */}
            <div className="text-center lg:text-left">
              <Badge className="bg-white/10 text-white/90 border-white/20 mb-6 py-1.5 px-4">
                <Sparkles className="w-3.5 h-3.5 mr-2" />
                {t.hero.badge}
              </Badge>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                <span className="text-white">{t.hero.title1}</span>
                <br />
                <span className="text-[#34B772]">{t.hero.title2}</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                {t.hero.subtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
                <Button 
                  size="lg"
                  className="bg-[#34B772] hover:bg-[#2da366] text-white text-lg px-8 h-14 font-semibold"
                  onClick={() => navigate('/register')}
                >
                  {t.hero.cta}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
              
              <p className="text-sm text-slate-400 flex items-center justify-center lg:justify-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#34B772]" />
                {t.hero.trust}
              </p>
            </div>

            {/* Right - Phone Mockup - More realistic */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute -inset-8 bg-[#34B772]/15 rounded-[60px] blur-3xl" />
                
                {/* Phone frame - iPhone style */}
                <div className="relative w-[260px] sm:w-[290px] bg-gray-900 rounded-[50px] p-[10px] shadow-2xl">
                  {/* Screen */}
                  <div className="bg-white rounded-[40px] overflow-hidden relative" style={{ aspectRatio: '9/19.5' }}>
                    {/* Dynamic Island / Notch */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full z-20" />
                    
                    {/* Status bar */}
                    <div className="relative z-10 pt-2 px-8 flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-900">11:30</span>
                      <div className="flex items-center gap-1">
                        <div className="flex gap-0.5">
                          <div className="w-1 h-1 bg-slate-900 rounded-full" />
                          <div className="w-1 h-1 bg-slate-900 rounded-full" />
                          <div className="w-1 h-1 bg-slate-400 rounded-full" />
                          <div className="w-1 h-1 bg-slate-400 rounded-full" />
                        </div>
                        <svg className="w-4 h-3 text-slate-900" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
                        </svg>
                        <div className="w-6 h-3 bg-slate-900 rounded-sm relative">
                          <div className="absolute right-0.5 top-0.5 bottom-0.5 left-1 bg-[#34B772] rounded-sm" />
                        </div>
                      </div>
                    </div>
                    
                    {/* App header */}
                    <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100 mt-4">
                      <div className="flex items-center gap-3">
                        <Menu className="w-5 h-5 text-slate-600" />
                        <img 
                          src="/logo-texhub.png" 
                          alt="TexHub" 
                          className="h-6 object-contain"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 bg-slate-100 rounded-full px-2 py-1">
                          <span className="text-xs">🇲🇽</span>
                          <Globe className="w-3 h-3 text-slate-500" />
                        </div>
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                          <span className="text-xs">👤</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Dashboard content */}
                    <div className="p-3 bg-slate-50">
                      {/* Stats cards */}
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="bg-white rounded-xl p-3 shadow-sm">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mb-2">
                            <Package className="w-4 h-4 text-blue-600" />
                          </div>
                          <p className="text-[10px] text-slate-500">{isUSA ? 'Published' : 'Productos publicados'}</p>
                          <p className="text-xl font-bold text-slate-900">9</p>
                          <p className="text-[8px] text-slate-400">{isUSA ? 'in catalog' : 'en tu catálogo'}</p>
                        </div>
                        <div className="bg-white rounded-xl p-3 shadow-sm">
                          <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mb-2">
                            <MessageSquare className="w-4 h-4 text-emerald-600" />
                          </div>
                          <p className="text-[10px] text-slate-500">{isUSA ? 'Quotes' : 'Cotizaciones'}</p>
                          <p className="text-xl font-bold text-slate-900">2</p>
                          <p className="text-[8px] text-slate-400">{isUSA ? 'received' : 'recibidas'}</p>
                        </div>
                      </div>
                      
                      {/* Quick actions */}
                      <p className="text-[10px] font-semibold text-slate-600 mb-2">{isUSA ? 'Quick actions' : 'Acciones rápidas'}</p>
                      <div className="space-y-2">
                        <div className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#34B772]/20 rounded-xl flex items-center justify-center">
                            <span className="text-[#34B772] text-lg">+</span>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-800">{isUSA ? 'Add product' : 'Agregar producto'}</p>
                            <p className="text-[9px] text-slate-400">{isUSA ? 'Expand catalog' : 'Amplía tu catálogo'}</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                            <Zap className="w-5 h-5 text-amber-500" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-800">{isUSA ? 'Post surplus' : 'Publicar saldo'}</p>
                            <p className="text-[9px] text-slate-400">{isUSA ? 'Sell fast' : 'Vende inventario rápido'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Bottom nav */}
                    <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-2 py-2 pb-4">
                      <div className="flex justify-around">
                        {[
                          { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>, label: 'Inicio', active: true },
                          { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>, label: 'Buscar', active: false },
                          { icon: <Store className="w-5 h-5" />, label: 'Tienda', active: false },
                          { icon: <MessageSquare className="w-5 h-5" />, label: 'Mensajes', active: false },
                          { icon: <Zap className="w-5 h-5" />, label: 'Saldos', active: false }
                        ].map((item, i) => (
                          <div key={i} className={`flex flex-col items-center ${item.active ? 'text-[#1E4C8E]' : 'text-slate-400'}`}>
                            {item.icon}
                            <span className="text-[8px] mt-0.5 font-medium">{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TexHub AI Section - HIGHLIGHTED RIGHT AFTER HERO */}
      <section id="ai" className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-amber-200/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 mb-4 px-4 py-1 text-sm">
              <Sparkles className="w-4 h-4 mr-2" />
              {isUSA ? '✨ NEW: AI-Powered Tools' : '✨ NUEVO: Herramientas con IA'}
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              TexHub AI
            </h2>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto font-medium">
              {isUSA 
                ? 'Revolutionary artificial intelligence tools to design, optimize and analyze your textile products' 
                : 'Herramientas revolucionarias de inteligencia artificial para diseñar, optimizar y analizar tus productos textiles'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {t.ai.tools.map((tool, i) => (
              <div 
                key={i}
                className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-2 border-amber-200 hover:border-amber-400 transition-all hover:shadow-xl hover:-translate-y-1 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Sparkles className="w-7 h-7 text-white" />
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 border-amber-300 font-semibold">
                    {tool.credits} {isUSA ? 'credits' : 'créditos'}
                  </Badge>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {tool.name}
                </h3>
                <p className="text-slate-600">
                  {tool.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all"
              onClick={() => navigate('/register')}
            >
              <Sparkles className="w-6 h-6 mr-2" />
              {isUSA ? 'Try TexHub AI Free' : 'Prueba TexHub AI Gratis'}
            </Button>
            <p className="text-amber-700 mt-4 font-medium">
              {isUSA ? '🎁 Start with 20 free credits!' : '🎁 ¡Comienza con 20 créditos gratis!'}
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t.features.title}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t.features.subtitle}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.sections.items.map((item, i) => (
              <div 
                key={i}
                className={`p-6 rounded-2xl transition-all hover:shadow-lg ${
                  i === 0 || i === 1 
                    ? 'bg-gradient-to-br from-slate-900 to-slate-800 text-white' 
                    : 'bg-white border border-slate-200'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  i === 0 || i === 1 ? 'bg-[#34B772]' : 'bg-slate-100'
                }`}>
                  <span className={i === 0 || i === 1 ? 'text-white' : 'text-[#1E4C8E]'}>
                    {item.icon}
                  </span>
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${i === 0 || i === 1 ? 'text-white' : 'text-slate-900'}`}>
                  {item.title}
                </h3>
                <p className={i === 0 || i === 1 ? 'text-slate-300' : 'text-slate-600'}>
                  {item.desc}
                </p>
                {(i === 0 || i === 1) && (
                  <Badge className="mt-4 bg-[#34B772]/20 text-[#34B772] border-0">
                    {isUSA ? 'Popular' : 'Popular'}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {isUSA ? 'Simple, transparent pricing' : 'Precios simples y transparentes'}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {isUSA ? 'Choose the plan that best fits your business' : 'Elige el plan que mejor se adapte a tu negocio'}
            </p>
          </div>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Free Plan */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{isUSA ? 'Free' : 'Gratis'}</h3>
              <p className="text-slate-600 mb-4">{isUSA ? 'To try the platform' : 'Para probar la plataforma'}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">{isUSA ? '$0' : '$0'}</span>
                <span className="text-slate-500">/{isUSA ? 'month' : 'mes'}</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-[#34B772]" />
                  {isUSA ? 'Browse catalog' : 'Explorar catálogo'}
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-[#34B772]" />
                  {isUSA ? 'Contact suppliers' : 'Contactar proveedores'}
                </li>
                <li className="flex items-center gap-2 text-slate-400">
                  <span className="w-5 h-5 text-center">—</span>
                  <span className="line-through">{isUSA ? 'Online store' : 'Tienda en línea'}</span>
                </li>
                <li className="flex items-center gap-2 text-amber-600 font-medium">
                  <Sparkles className="w-5 h-5" />
                  20 {isUSA ? 'AI Credits to try' : 'Créditos IA para probar'}
                </li>
              </ul>
              <Button variant="outline" className="w-full" onClick={() => navigate('/register')}>
                {isUSA ? 'Start free' : 'Comenzar gratis'}
              </Button>
            </div>

            {/* PRO Plan - Highlighted */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <Badge className="bg-[#34B772] text-white border-0 px-4">
                  {isUSA ? 'Most Popular' : 'Más Popular'}
                </Badge>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">PRO</h3>
              <p className="text-slate-300 mb-4">{isUSA ? 'For growing businesses' : 'Para negocios en crecimiento'}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">{isUSA ? '$35' : '$599'}</span>
                <span className="text-slate-400">/{isUSA ? 'month' : 'mes'}</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-white">
                  <CheckCircle2 className="w-5 h-5 text-[#34B772]" />
                  {isUSA ? 'Online store' : 'Tienda en línea'}
                </li>
                <li className="flex items-center gap-2 text-white">
                  <CheckCircle2 className="w-5 h-5 text-[#34B772]" />
                  {isUSA ? 'Unlimited products' : 'Productos ilimitados'}
                </li>
                <li className="flex items-center gap-2 text-white">
                  <CheckCircle2 className="w-5 h-5 text-[#34B772]" />
                  {isUSA ? 'Advanced analytics' : 'Analytics avanzados'}
                </li>
                <li className="flex items-center gap-2 text-amber-400 font-semibold">
                  <Sparkles className="w-5 h-5" />
                  100 {isUSA ? 'AI Credits/month' : 'Créditos IA/mes'}
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-sm pl-7">
                  {isUSA ? 'Fashion Designer, Visual Search, Photo Optimizer, TexTrend AI' : 'Diseñador de Modas, Buscador Visual, Optimizador de Fotos, TexTrend AI'}
                </li>
              </ul>
              <Button className="w-full bg-[#34B772] hover:bg-[#2da366] text-white" onClick={() => navigate('/register')}>
                {isUSA ? 'Start 7-day trial' : 'Prueba 7 días gratis'}
              </Button>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{isUSA ? 'Enterprise' : 'Empresarial'}</h3>
              <p className="text-slate-600 mb-4">{isUSA ? 'For large teams' : 'Para equipos grandes'}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">{isUSA ? '$89' : '$1,499'}</span>
                <span className="text-slate-500">/{isUSA ? 'month' : 'mes'}</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-[#34B772]" />
                  {isUSA ? 'Everything in PRO' : 'Todo lo de PRO'}
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-[#34B772]" />
                  {isUSA ? 'Multi-user access' : 'Acceso multi-usuario'}
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-[#34B772]" />
                  {isUSA ? 'Priority support' : 'Soporte prioritario'}
                </li>
                <li className="flex items-center gap-2 text-amber-600 font-semibold">
                  <Sparkles className="w-5 h-5" />
                  500 {isUSA ? 'AI Credits/month' : 'Créditos IA/mes'}
                </li>
              </ul>
              <Button variant="outline" className="w-full" onClick={() => navigate('/register')}>
                {isUSA ? 'Contact sales' : 'Comenzar ahora'}
              </Button>
            </div>
          </div>

          {/* Credit Packs */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-200">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                <Sparkles className="w-6 h-6 inline mr-2 text-amber-500" />
                {isUSA ? 'Need more AI credits?' : '¿Necesitas más créditos IA?'}
              </h3>
              <p className="text-slate-600">
                {isUSA ? 'Purchase additional credit packs anytime' : 'Compra paquetes de créditos adicionales en cualquier momento'}
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 text-center border border-amber-100 hover:border-amber-300 transition-colors">
                <p className="text-3xl font-bold text-amber-600 mb-1">50</p>
                <p className="text-slate-500 text-sm mb-3">{isUSA ? 'credits' : 'créditos'}</p>
                <p className="text-xl font-bold text-slate-900">{isUSA ? '$5.99 USD' : '$99 MXN'}</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center border-2 border-amber-400 relative hover:shadow-lg transition-shadow">
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white border-0">
                  {isUSA ? 'Best value' : 'Mejor valor'}
                </Badge>
                <p className="text-3xl font-bold text-amber-600 mb-1">150</p>
                <p className="text-slate-500 text-sm mb-3">{isUSA ? 'credits' : 'créditos'}</p>
                <p className="text-xl font-bold text-slate-900">{isUSA ? '$14.99 USD' : '$249 MXN'}</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center border border-amber-100 hover:border-amber-300 transition-colors">
                <p className="text-3xl font-bold text-amber-600 mb-1">400</p>
                <p className="text-slate-500 text-sm mb-3">{isUSA ? 'credits' : 'créditos'}</p>
                <p className="text-xl font-bold text-slate-900">{isUSA ? '$32.99 USD' : '$549 MXN'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Store Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-[#1E4C8E]/10 text-[#1E4C8E] border-0 mb-4">
                <Store className="w-3.5 h-3.5 mr-2" />
                {t.store.badge}
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                {t.store.title}
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                {t.store.subtitle}
              </p>
              <ul className="space-y-4 mb-8">
                {t.store.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#34B772] flex-shrink-0" />
                    <span className="text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="bg-[#1E4C8E] hover:bg-[#163a6e]"
                onClick={() => navigate('/register')}
              >
                {isUSA ? 'Create my store' : 'Crear mi tienda'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            
            {/* Store mockup */}
            <div className="relative">
              <div className="bg-slate-900 rounded-2xl p-4 shadow-2xl">
                <div className="bg-white rounded-xl overflow-hidden">
                  <div className="bg-gradient-to-r from-[#1E4C8E] to-[#2d5a9e] p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center">
                        <Store className="w-8 h-8 text-[#1E4C8E]" />
                      </div>
                      <div className="text-white">
                        <h4 className="font-bold text-lg">{isUSA ? 'Your Store' : 'Tu Tienda'}</h4>
                        <p className="text-white/70 text-sm">texhub.com.mx/tu-negocio</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-3 gap-3">
                      {[1,2,3,4,5,6].map(i => (
                        <div key={i} className="aspect-square bg-slate-100 rounded-lg" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-[#34B772] text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg">
                {isUSA ? 'No commissions' : 'Sin comisiones'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Surplus Section */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Images */}
            <div className="relative order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="relative rounded-2xl overflow-hidden aspect-[3/4]">
                  <img 
                    src="https://images.unsplash.com/photo-1558171813-4c088753af8f?auto=format&fit=crop&w=600&q=80"
                    alt="Fabric rolls"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <Badge className="bg-amber-500 text-white border-0">
                      {isUSA ? 'Fabric' : 'Tela'}
                    </Badge>
                  </div>
                </div>
                <div className="relative rounded-2xl overflow-hidden aspect-[3/4] mt-8">
                  <img 
                    src="https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&w=600&q=80"
                    alt="Apparel"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <Badge className="bg-purple-500 text-white border-0">
                      {isUSA ? 'Apparel' : 'Ropa'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2">
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
                <Tag className="w-3.5 h-3.5 mr-2" />
                {t.surplus.badge}
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                {t.surplus.title}
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                {t.surplus.subtitle}
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {t.surplus.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <span className="text-slate-200">{f}</span>
                  </div>
                ))}
              </div>
              <Button 
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => navigate('/surplus')}
              >
                {isUSA ? 'View surplus' : 'Ver saldos'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="py-12 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16 text-slate-500">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#34B772]" />
              <span className="font-medium">{isUSA ? 'Secure & trusted' : 'Seguro y confiable'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#34B772]" />
              <span className="font-medium">{isUSA ? 'Fast setup' : 'Configura en minutos'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#34B772]" />
              <span className="font-medium">{isUSA ? 'Business network' : 'Red de negocios'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#34B772]" />
              <span className="font-medium">🇲🇽 🇺🇸</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#1E4C8E] to-[#163a6e]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t.cta.title}
          </h2>
          <p className="text-xl text-white/80 mb-10">
            {t.cta.subtitle}
          </p>
          <Button 
            size="lg"
            className="bg-[#34B772] hover:bg-[#2da366] text-white text-lg px-10 h-14 font-semibold"
            onClick={() => navigate('/register')}
          >
            {t.cta.button}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <TexHubLogo size="small" />
              <span className="text-slate-400">|</span>
              <p className="text-slate-400">{t.footer.tagline}</p>
            </div>
            <p className="text-slate-400 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              {t.footer.available}
            </p>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            © 2025 TexHub. {isUSA ? 'All rights reserved.' : 'Todos los derechos reservados.'}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
